import { useEffect, useMemo, useState } from "react";
import { categories, updateList, type Manga } from "@/mocks/manga";

const tabs = ["全部", ...categories.map((c) => c.name)];

export default function UpdateSection({ initialKeyword = "", initialCategory = "全部" }: { initialKeyword?: string; initialCategory?: string }) {
  const [active, setActive] = useState("全部");
  const [keyword, setKeyword] = useState(initialKeyword);
  const [selected, setSelected] = useState<Manga | null>(null);

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  useEffect(() => {
    if (tabs.includes(initialCategory)) setActive(initialCategory);
  }, [initialCategory]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return updateList.filter((m) => {
      const matchCat = active === "全部" || m.category === active;
      const matchKw = !kw || m.title.includes(kw) || m.tags.some((t) => t.includes(kw));
      return matchCat && matchKw;
    });
  }, [active, keyword]);

  return (
    <section id="update" className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium uppercase tracking-widest text-primary-600">
            <i className="ri-refresh-line"></i> LATEST
          </span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">最新更新 · 无删减同步追更</h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
            连载中的火影同人漫画实时同步最新章节，全部免费阅读，无删减完整呈现。
          </p>
        </div>
        <label className="flex w-full max-w-xs items-center gap-2 rounded-lg bg-background-100 px-3 py-2.5 ring-1 ring-background-200/70">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="在本栏目中搜索…"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </label>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActive(tab)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active === tab ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 ring-1 ring-background-200/70 hover:bg-primary-100/60"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg bg-background-100 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
          <p className="mt-3 text-sm text-foreground-600">没有找到匹配的火影同人漫画，换个关键词试试吧。</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <article
              key={m.id}
              onClick={() => setSelected(m)}
              className="group flex cursor-pointer gap-4 rounded-lg bg-background-100 p-3 ring-1 ring-background-200/70 transition-transform hover:-translate-y-1"
            >
              <div className="relative w-24 shrink-0 overflow-hidden rounded-md sm:w-28">
                <img src={m.cover} alt={`${m.title} - 火影同人漫画封面`} className="h-32 w-full object-cover object-top sm:h-36" />
                <span className="absolute left-1.5 top-1.5 whitespace-nowrap rounded bg-black/55 px-1.5 py-0.5 text-[10px] text-background-50">
                  {m.status}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="line-clamp-2 font-heading text-base font-bold text-foreground-950">{m.title}</h3>
                  <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-accent-600">
                    <i className="ri-star-fill"></i> {m.score.toFixed(1)}
                  </span>
                </div>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{m.intro}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.tags.map((t) => (
                    <span key={t} className="whitespace-nowrap rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-background-200/70 pt-2.5 text-xs text-foreground-500">
                  <span className="whitespace-nowrap">{m.latest}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i> {m.views}
                  </span>
                  <span className="whitespace-nowrap font-medium text-primary-600">
                    查看详情 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background-950/60 px-4" onClick={() => setSelected(null)}>
          <div className="flex w-full max-w-2xl flex-col gap-5 overflow-hidden rounded-lg bg-background-50 p-5 sm:flex-row md:p-6" onClick={(e) => e.stopPropagation()}>
            <img src={selected.cover} alt={`${selected.title} - 火影同人漫画`} className="h-48 w-full rounded-md object-cover object-top sm:h-64 sm:w-44" />
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-xl font-bold text-foreground-950">{selected.title}</h3>
                <span className={`whitespace-nowrap rounded px-2 py-0.5 text-xs ${selected.status === "连载中" ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-900"}`}>
                  {selected.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-foreground-500">{selected.category} · {selected.latest} · {selected.views} 人气</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{selected.intro}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {selected.tags.map((t) => (
                  <span key={t} className="whitespace-nowrap rounded bg-secondary-100 px-2 py-1 text-xs text-secondary-900">{t}</span>
                ))}
              </div>
              <div className="mt-auto flex items-center justify-between pt-4">
                <span className="flex items-center gap-1 text-sm font-semibold text-accent-600">
                  <i className="ri-star-fill"></i> {selected.score.toFixed(1)} 分
                </span>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  开始阅读
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}